import { FC, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { Close, ExpandMore, OpenInNew } from "@mui/icons-material"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { store } from "@core/store"
import {
  guestBotApi,
  useBotsListQuery,
  useProvisionListQuery,
} from "../../store"
import {
  getProvisionErrorText,
  getProvisionErrorTooltip,
  isProvisionActive,
  isProvisionFailedForUi,
  PROVISION_POLL_MS,
} from "../../utils/provisionStatus"
import { ProvisionLocalError } from "./types"

interface IProps {
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  localErrors: ProvisionLocalError[]
  onDismissError: (key: string) => void
}

interface Row {
  key: string
  title: string
  username: string | null
  status: "active" | "failed"
  chipText: string
  tooltip: string
  botId: number | null
  createdAt: string | null
  canDismiss: boolean
  isLocal: boolean
}

/** Секция «Создание ботов»: активные + неудачные записи (completed не показываем). */
export const ProvisionSection: FC<IProps> = ({
  expanded,
  onExpandedChange,
  localErrors,
  onDismissError,
}) => {
  const [pollingInterval, setPollingInterval] = useState(PROVISION_POLL_MS)
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())
  const { data, isError, error } = useProvisionListQuery(undefined, {
    pollingInterval,
  })
  const { data: botsData } = useBotsListQuery()
  const navigate = useNavigate()
  const seenCompletedRef = useRef<Set<string>>(new Set())

  // Поллинг 30 сек, пока есть активные (спека §3.2). Ошибка запроса — стоп + Alert.
  useEffect(() => {
    if (isError) {
      setPollingInterval(0)
      return
    }
    const hasActive = (data?.items ?? []).some((p) =>
      isProvisionActive(p.status, p.lastError),
    )
    setPollingInterval(hasActive ? PROVISION_POLL_MS : 0)
  }, [data, isError])

  // Новые completed (не виденные ранее) — обновить список ботов.
  useEffect(() => {
    const completed = (data?.items ?? []).filter(
      (p) => p.status === "completed" && !seenCompletedRef.current.has(p.id),
    )
    if (completed.length === 0) return
    completed.forEach((p) => seenCompletedRef.current.add(p.id))
    store.dispatch(guestBotApi.util.invalidateTags(["Bots"]))
  }, [data])

  const rows = useMemo<Row[]>(() => {
    const fromServer: Row[] = (data?.items ?? [])
      .filter(
        (p) =>
          isProvisionActive(p.status, p.lastError) ||
          (isProvisionFailedForUi(p.status, p.lastError) &&
            !hiddenIds.has(p.id)),
      )
      .map((p) => {
        const failed = isProvisionFailedForUi(p.status, p.lastError)
        return {
          key: p.id,
          title: p.title,
          username: p.usernameSnapshot ?? p.username,
          status: failed ? "failed" : "active",
          chipText: failed ? getProvisionErrorText(p.lastError) : "Создаётся…",
          tooltip: failed
            ? getProvisionErrorTooltip(p.lastError, p.adopted)
            : "",
          botId: p.botId,
          createdAt: p.createdAt,
          canDismiss: failed,
          isLocal: false,
        }
      })
    const fromLocal: Row[] = localErrors.map((e) => ({
      key: e.key,
      title: e.title,
      username: e.username,
      status: "failed",
      chipText: e.text,
      tooltip: e.text,
      botId: null,
      createdAt: null,
      canDismiss: true,
      isLocal: true,
    }))
    return [...fromServer, ...fromLocal]
  }, [data, localErrors, hiddenIds])

  const activeCount = rows.filter((r) => r.status === "active").length
  const failedCount = rows.length - activeCount

  const uuidFor = (botId: number | null) =>
    botId == null
      ? undefined
      : botsData?.items.find((b) => b.botId === botId)?.id

  if (rows.length === 0 && !isError) return null

  return (
    <>
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}
      <Accordion expanded={expanded} onChange={(_, v) => onExpandedChange(v)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Создание ботов</Typography>
          {activeCount > 0 && (
            <Chip
              size="small"
              color="info"
              label={`Активные: ${activeCount}`}
              sx={{ ml: 1 }}
            />
          )}
          {failedCount > 0 && (
            <Chip
              size="small"
              color="error"
              label={`Ошибки: ${failedCount}`}
              sx={{ ml: 1 }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Имя</TableCell>
                <TableCell>Юзернейм</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const uuid = uuidFor(row.botId)
                return (
                  <TableRow key={row.key}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>
                      {row.username ? `@${row.username}` : "—"}
                    </TableCell>
                    <TableCell>
                      {row.status === "active" ? (
                        <Chip
                          size="small"
                          color="info"
                          icon={<CircularProgress size={14} color="inherit" />}
                          label="Создаётся…"
                        />
                      ) : (
                        <Tooltip title={row.tooltip} arrow>
                          <Chip
                            size="small"
                            color="error"
                            label={row.chipText}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.createdAt ? formatDateAndTime(row.createdAt) : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {uuid && (
                        <Tooltip title="Открыть бота" arrow>
                          <IconButton
                            size="small"
                            aria-label="Открыть бота"
                            onClick={() =>
                              navigate(`/telegram/guest-bots/${uuid}`)
                            }
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.canDismiss && (
                        <Tooltip title="Скрыть" arrow>
                          <IconButton
                            size="small"
                            aria-label="Скрыть"
                            onClick={() =>
                              row.isLocal
                                ? onDismissError(row.key)
                                : setHiddenIds((prev) =>
                                    new Set(prev).add(row.key),
                                  )
                            }
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    </>
  )
}
